import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Pause, Loader2 } from "lucide-react";
import { requestNarration, BACKEND_URL } from "../../lib/api";

/**
 * NarrationPlayer
 *  - Loads (and caches server-side) the narration for a given page.
 *  - Elegant pill button, tucked at the top-right of the book page.
 *  - Auto-pauses when the parent page changes (via `pageKey` change).
 */
export default function NarrationPlayer({ pageId, pageKey, text }) {
  const audioRef = useRef(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | playing | paused | error
  const [audioUrl, setAudioUrl] = useState(null);
  const trimmed = (text || "").trim();

  // Reset & pause whenever the page changes.
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setAudioUrl(null);
    setStatus("idle");
  }, [pageKey]);

  if (!trimmed) return null;

  const handleClick = async () => {
    if (status === "playing") {
      audioRef.current?.pause();
      setStatus("paused");
      return;
    }
    if (status === "paused" || status === "ready") {
      try {
        await audioRef.current?.play();
        setStatus("playing");
      } catch {
        setStatus("error");
      }
      return;
    }
    // idle or error → fetch and play
    setStatus("loading");
    try {
      const { audio_url } = await requestNarration({ page_id: pageId, text: trimmed });
      const fullUrl = `${BACKEND_URL}${audio_url}`;
      setAudioUrl(fullUrl);
      // Wait for the audio element to receive the src, then play
      setTimeout(async () => {
        if (audioRef.current) {
          audioRef.current.src = fullUrl;
          try {
            await audioRef.current.play();
            setStatus("playing");
          } catch {
            setStatus("ready");
          }
        }
      }, 40);
    } catch (e) {
      console.error("Narration failed", e);
      setStatus("error");
    }
  };

  const isPlaying = status === "playing";
  const isLoading = status === "loading";

  return (
    <div className="absolute top-3 right-3 md:top-5 md:right-6 z-10">
      <motion.button
        onClick={handleClick}
        data-testid="narration-toggle"
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        className="group flex items-center gap-2 rounded-full border border-line bg-bg/85 backdrop-blur px-2 sm:px-3.5 py-1.5 sm:py-2 shadow-[0_6px_20px_-10px_rgba(28,28,28,0.25)] hover:border-forest/40 transition-colors"
        aria-label={isPlaying ? "Pause narration" : "Listen to this page in Krishna's voice"}
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-forest text-bg">
          {isLoading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : isPlaying ? (
            <Pause className="h-3.5 w-3.5" />
          ) : (
            <Play className="h-3.5 w-3.5 translate-x-[1px]" />
          )}
        </span>

        {/* mini waveform while playing */}
        <AnimatePresence mode="wait">
          {isPlaying ? (
            <motion.span
              key="waves"
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: "auto" }}
              exit={{ opacity: 0, width: 0 }}
              className="flex items-center gap-[3px] overflow-hidden"
            >
              {[0, 1, 2, 3].map((i) => (
                <motion.span
                  key={i}
                  className="block w-[2px] rounded-full bg-forest"
                  animate={{ height: ["6px", "14px", "6px"] }}
                  transition={{ duration: 0.9, repeat: Infinity, ease: "easeInOut", delay: i * 0.12 }}
                />
              ))}
            </motion.span>
          ) : (
            <motion.span
              key="label"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="hidden sm:inline font-sans text-[10px] uppercase tracking-mega text-ink-muted group-hover:text-forest transition-colors whitespace-nowrap"
            >
              {status === "error" ? "Try again" : status === "loading" ? "Loading" : "Listen in Krishna's voice"}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      {/* Hidden native player */}
      <audio
        ref={audioRef}
        onEnded={() => setStatus("ready")}
        onPause={() => {
          if (status === "playing") setStatus("paused");
        }}
        preload="none"
        className="hidden"
      />
    </div>
  );
}
